import { formatLocalizedNumber } from 'lib-analytics-ui';
import { Timer } from 'lib-shared-legacy-components';
import moment from 'moment-timezone';
import { memo, useMemo } from 'react';

import { dashboardApi } from 'apis/dashboard';
import {
  TvDashboardMetric,
  TvDashboardMetricItem,
} from 'app/views/tvDashboard/components/metric';
import { mediaAttributes } from 'utils/channels/media';
import {
  convertMetricsToMap,
  useDashboardAgentPresenceMetric,
  useDashboardRefetchEffect,
} from 'utils/dashboard';

import styles from './tvOrganizationDashboardMetrics.module.scss';

export const TvOrganizationDashboardMetrics = memo(
  function TvOrganizationDashboardMetrics(): JSX.Element {
    const { metrics, isLoading, refetch } =
      dashboardApi.useReadOrganizationDashboardMetricsQuery(undefined, {
        // Automatically refetch the dashboard data via an HTTP request every hour to ensure the
        // access/refresh tokens are refreshed and the TV dashboard remains logged in indefinitely.
        pollingInterval: 1000 * 60 * 60,
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          metrics: convertMetricsToMap(
            data?.data.organization_metrics[0]?.metrics,
          ),
        }),
      });

    useDashboardRefetchEffect({ refetch });

    const { online, available } = useDashboardAgentPresenceMetric(
      metrics?.agent_presence?.agents,
    );

    const metricItems = useMemo((): readonly TvDashboardMetricItem[] => {
      const inQueue = metrics.pending_conversations?.in_queue ?? 0;
      const open = metrics.pending_conversations?.open ?? 0;
      const total = inQueue + open;

      return [
        {
          title: 'Waiting',
          stat: formatLocalizedNumber(inQueue),
          color: 'orange',
        },
        {
          title: 'Total pending',
          stat: formatLocalizedNumber(total),
        },

        {
          title: 'Assigned',
          stat: formatLocalizedNumber(open),
          color: 'purple',
        },
        {
          title: 'Longest queued time',
          stat: metrics.queued_time?.longest_queued_time ? (
            <Timer
              asCounter={false}
              start
              startAt={moment(
                metrics.queued_time.longest_queued_time,
              ).toISOString()}
              textFitProps={{ max: 70 }}
            />
          ) : (
            '00:00:00'
          ),
          color: 'green',
        },
        {
          title: 'Answered',
          stat: formatLocalizedNumber(
            metrics.daily_conversations?.total_answered ?? 0,
          ),
          color: 'green',
        },
        {
          title: 'Received',
          stat: formatLocalizedNumber(
            metrics.daily_conversations?.total_received ?? 0,
          ),
        },
        {
          title: 'Abandoned',
          stat:
            typeof metrics.daily_conversations?.total_abandoned === 'number'
              ? formatLocalizedNumber(
                  metrics.daily_conversations.total_abandoned,
                )
              : '-',
          color: 'red',
        },
        {
          title: 'Avg. assignment time',
          stat: metrics.daily_conversations?.average_assignment_time ? (
            <Timer
              asCounter
              start={false}
              startAt={
                metrics.daily_conversations.average_assignment_time / 1000
              }
              textFitProps={{ max: 70 }}
            />
          ) : (
            '00:00:00'
          ),
          color: 'green',
        },
        ...Object.entries(mediaAttributes).map(([media, { name }]) => ({
          title: `${name} agents`,
          stat: `${available.media_type[media]?.total ?? 0} / ${online.media_type[media]?.total ?? 0}`,
        })),
        {
          title: 'Avg. response time',
          stat: metrics.response_time?.average_response_time ? (
            <Timer
              asCounter
              start={false}
              startAt={metrics.response_time.average_response_time / 1000}
              textFitProps={{ max: 70 }}
            />
          ) : (
            '00:00:00'
          ),
          color: 'green',
        },
      ];
    }, [metrics, online, available]);

    return (
      <div className={styles.grid}>
        {metricItems.map((metric, index) => (
          <TvDashboardMetric
            key={index}
            className={styles.item}
            metric={metric}
            isLoading={isLoading}
          />
        ))}
      </div>
    );
  },
);
